import React, { useRef } from 'react';
import AsyncSelect from 'react-select/async';
import { Icon } from '@lomarengas/design-system';
import tagsService from '../../api/tagsService';
import { Tag } from '../../../features/tags/tagsSlice';

interface TagSelectorProps {
  filterActiveAccommodations?: boolean;
  Placeholder?: string;
  Subtitle: string;
  Title: string;
  onTagSelected?: (selectedTagId: string) => void;
}

interface OptionType {
  value: string;
  label: string;
}

const TagSelector = ({
  filterActiveAccommodations,
  Placeholder,
  Subtitle,
  Title,
  onTagSelected,
}: TagSelectorProps) => {
  const cachedOptions = useRef<OptionType[] | null>(null);

  const handleSelectTagChange = (selectedOption: OptionType) => {
    if (selectedOption && selectedOption.label && selectedOption.value) {
      const selectedValue = selectedOption.value;
      onTagSelected(selectedValue);
    } else {
      onTagSelected(null);
    }
  };

  const formatOptions = (options: Tag[] | undefined): OptionType[] => {
    if (!options || options.length === 0) {
      return [];
    }

    return options.map(option => ({
      value: option.id,
      label: option.name,
    }));
  };

  const loadOptions = async (inputValue: string): Promise<OptionType[]> => {
    if (cachedOptions.current === null) {
      const tags = await tagsService.fetchAccommodationTags(
        filterActiveAccommodations,
      );
      cachedOptions.current = formatOptions(tags);
    }
    return cachedOptions.current.filter(option =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  return (
    <>
      <div className="d-flex align-items-start align-items-sm-center mb-24 mb-xl-32">
        <div className="d-block bg-primary fs-3 me-24 px-16 py-8 rounded text-center text-white">
          <Icon Name="tags-fill" />
        </div>
        <div className="flex-grow-1">
          <h2 className="h5 mb-0">{Title}</h2>
          <p className="text-gray-600 mb-0">{Subtitle}</p>
        </div>
      </div>
      <AsyncSelect
        styles={{
          control: baseStyles => ({
            ...baseStyles,
            borderColor: '#ced4da',
            borderRadius: 6,
          }),
          dropdownIndicator: baseStyles => ({
            ...baseStyles,
            visibility: 'hidden',
          }),
          indicatorSeparator: baseStyles => ({
            ...baseStyles,
            visibility: 'hidden',
          }),
        }}
        classNames={{
          control: state => 'form-control form-control-search ps-48 pe-8',
        }}
        inputId="tagId"
        name="tagId"
        placeholder={Placeholder}
        isClearable
        isSearchable
        defaultOptions
        loadOptions={loadOptions}
        onChange={option => handleSelectTagChange(option)}
      />
    </>
  );
};

TagSelector.defaultProps = {
  Placeholder: 'Hae tagin nimellä...',
};

export default TagSelector;
export { TagSelector };
export type { TagSelectorProps };
